import { TechnologyItem } from '@tribal-worldwide-london/fa-product-service-vwg';
import { VideoModal } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  Breakpoints,
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
  styled,
  useTheme,
} from '@volkswagen-onehub/components-core';
import { Checkmark } from '@volkswagen-onehub/icons-core';
import { useState } from 'react';
import {
  handleVideoEnded,
  handleVideoMilestone,
  handleVideoPause,
  handleVideoPlay,
} from '@/analytics/eventHelpers/video';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import { useFeatures } from '@/hooks/useFeatures';
import Box from '@/modules/components/Box/Box';
import { CTACardProps } from '@/modules/components/CTACard/CTACard';
import PageContainer from '@/modules/components/PageContainer/PageContainer';
import { NAVIGATION_HEIGHT } from '@/modules/constants/navigation';
import FeaturesCarousel from '@/modules/features/components/FeaturesCarousel/FeaturesCarousel';
import TyreInformation from '@/modules/features/components/TyreInformation/TyreInformation';
import {
  CarOptionsEndpoint,
  getCTACardSlidesPerPage,
  getCarOptionsData,
  getSelectedVideo,
  getSlidesPerPage,
  technologyItemState,
} from '@/modules/utils/storeUtils';
import { getImageUrl } from '@/modules/utils/themeUtils';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { DisclaimerText } from '@/styles';
import CTACardCarousel from '@/modules/features/single/CTACardCarousel/CTACardCarousel';

interface OptionalExtrasProps {
  extrasCodes?: string[];
  bespokeItemsCodes?: string[];
  technologyVideos?: TechnologyItem[];
  activeConfiguration: GlobalStoreStateConfiguration;
}

const OptionalExtrasSectionWrapper = styled.div(
  ({ theme }) => `
  background-color: ${theme.colors.button.primary.default};
  color: white;
  margin: 0 -${theme.size.static300};

  @media (min-width: ${Breakpoints.b960}px) {
    margin: 0 -${theme.size.static530};
  }
`
);

const OptionalExtrasSectionWrapperInner = styled.div(
  ({ theme }) => `
  background-blend-mode: multiply;
  background: linear-gradient(
    269.97deg,
    rgba(0, 0, 0, 0.3) 0.03%,
    rgba(0, 0, 0, 0) 49.52%,
    rgba(10, 1, 1, 0.3) 99.97%
  );

  padding-top: ${theme.size.static400};

  @media (min-width: ${Breakpoints.b960}px) {
    padding-top: ${theme.size.static550};
  }
`
);

const OptionalExtrasContentWrapper = styled.div(
  ({ theme }) => `
  padding-bottom: ${theme.size.static400};

  @media (min-width: ${Breakpoints.b960}px) {
    padding-bottom: ${theme.size.static550};
  }
`
);

function OptionalExtras({
  extrasCodes,
  bespokeItemsCodes,
  technologyVideos,
  activeConfiguration,
}: OptionalExtrasProps) {
  const { contentManager, configManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { retailers } = useGlobalStore((state) => state.app);
  const [videoId, setVideoId] = useState('');
  const { getSpecItemLabel } = useFeatures();

  const getOptionsData = (data: string[], endpoint: CarOptionsEndpoint) => {
    return data?.map((code: string) => {
      const optionData = getCarOptionsData(activeConfiguration, code, endpoint);
      if (
        optionData &&
        'category2' in optionData &&
        optionData?.category2 === 'Tyres'
      ) {
        return {
          ...optionData,
          secondaryLink: {
            label: contentManager.get('ctas-tyre-information'),
            modalContent: (
              <TyreInformation configuration={activeConfiguration} />
            ),
          },
        };
      }

      return optionData;
    });
  };

 function getExtrasDataForConfiguration(
  extrasCodes: any[] | undefined,
  activeConfiguration: any
  ): any {
    const fetchContext = activeConfiguration.orderNumber ? 'stock' : 'config';
    let optionData: any[] = [];
    // Add exterior extra if available
    if (activeConfiguration.pdbData?.capi?.configuration?.exterior) {
      const exteriorExtra = activeConfiguration.pdbData?.options?.find(
        (option) => option.id === activeConfiguration.pdbData?.capi?.configuration?.exterior
      );
      if (exteriorExtra && exteriorExtra.availability === 'optional-extra') {
          optionData.push(exteriorExtra.id);
      }
    }

    // Add interior extra if available
    if (activeConfiguration.pdbData?.capi?.configuration?.interior) {
      const interiorExtra = activeConfiguration.pdbData?.options?.find(
        (option) => option.id === activeConfiguration.pdbData?.capi?.configuration?.interior
      );
      if (interiorExtra && interiorExtra.availability === 'optional-extra') {
        optionData.push(interiorExtra.id);
      }
    }
    optionData.forEach((item) => {
      if (!extrasCodes?.includes(item)) {
        extrasCodes = [...(extrasCodes ?? []), item];
      }
    });

  const extrasData = getOptionsData(
    extrasCodes || [],
    fetchContext
  );

  return extrasData;
}
  const extrasData = getExtrasDataForConfiguration(extrasCodes, activeConfiguration);
  const bespokeItemsData = getOptionsData(bespokeItemsCodes || [], 'pdb');
  
  const bespokeSelectedItems = bespokeItemsData.filter((item) => activeConfiguration?.bespokeSpecItems?.some(bespokeItem => bespokeItem.optionId === item.id && bespokeItem.selected === true));
    const featuresData = [
      ...extrasData.filter((item) => item?.visible === true),
      ...bespokeSelectedItems,
    ];

  const getPageTitle = () => {
    if (featuresData?.length && technologyVideos?.length) {
      return {
        normalTitle: contentManager.get(
          'extras-section-title-extras-videos-normal'
        ),
        boldTitle: contentManager.get(
          'extras-section-title-extras-videos-bold'
        ),
      };
    } else if (technologyVideos?.length) {
      return {
        normalTitle: contentManager.get('extras-section-title-videos-normal'),
        boldTitle: contentManager.get('extras-section-title-videos-bold'),
      };
    }

    return {
      normalTitle: contentManager.get('extras-section-title-normal'),
      boldTitle: contentManager.get('extras-section-title-bold'),
    };
  };

  const videosCTACards =
    (
      technologyVideos?.map((item: TechnologyItem) => {
        const videoUrl = item.assetCollection?.[0]?.url;
        if (!videoUrl) return;

        const availability = technologyItemState(item, activeConfiguration);

        return {
          title: item.name,
          text: item.subtitle,
          image: {
            desktopUrl: getImageUrl(
              configManager,
              item.imageFileName,
              'apis.assetsUrl'
            ),
          },
          isVideo: true,
          onClick: () => setVideoId(videoUrl || ''),
          label: getSpecItemLabel(availability),
        };
      }) as CTACardProps[]
    )?.filter((video) => video) || [];

  const extrasSlides = featuresData?.length
    ? getSlidesPerPage(featuresData.length, 3)
    : null;

  const techVideosSlides = technologyVideos?.length
    ? getCTACardSlidesPerPage(videosCTACards.length)
    : null;

  const analyticsCommonFields = {
    sectionName: SectionNames.SINGLE_PAGE_OPTIONAL_EXTRAS,
    configuration: activeConfiguration,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const selectedVideoDetails = getSelectedVideo(
    technologyVideos || [],
    videoId
  );

  const { normalTitle, boldTitle } = getPageTitle();

  const title = selectedVideoDetails?.name || null;

  const theme = useTheme();

  return (
    <OptionalExtrasSectionWrapper>
      <OptionalExtrasSectionWrapperInner>
        <PageContainer>
          {featuresData.length > 0 && (
            <OptionalExtrasContentWrapper>
              <Box
                display="flex"
                flexDirection="column"
                gap={DesignTokenSizeEnum.static150}
                b960={{
                  gap: DesignTokenSizeEnum.static250,
                  mb: DesignTokenSizeEnum.static300,
                }}
              >
                <Text
                  tag={TextTag.h2}
                  appearance={TokenTextAppearance.headline400}
                  color={TokenTextColor.inherit}
                >
                  {normalTitle}
                  <strong> {boldTitle}</strong>
                </Text>
                <Text
                  appearance={TokenTextAppearance.bigcopy100}
                  color={TokenTextColor.inherit}
                >
                  {contentManager.get('extras-section-subtitle')}
                </Text>
              </Box>
              <FeaturesCarousel
                id="optional-extras-carousel"
                ariaLabel="optional extras carousel"
                slidesPerPage={extrasSlides?.slidesPerPage}
                featuresData={featuresData}
                theme="inverted"
                hideNavigationDesktop={extrasSlides?.hideNavigation}
                footerStacked
                sectionName={SectionNames.SINGLE_PAGE_OPTIONAL_EXTRAS}
                decoratorImgIcon={<Checkmark />}
              />
              <DisclaimerText color={theme.colors.background.primary as any}>
                {featuresData.some((item) => item.excludedFromFinance) && (
                  <Box marginBottom={DesignTokenSizeEnum.static300}>
                    <Text
                      color={TokenTextColor.inherit}
                      appearance={TokenTextAppearance.copy100}
                    >
                      {'* ' + contentManager.get('optional-extras-item-text')}
                    </Text>
                  </Box>
                )}
                <Text
                  color={TokenTextColor.inherit}
                  appearance={TokenTextAppearance.copy100}
                >
                  {contentManager.get('prices-disclaimer-text')}
                </Text>
              </DisclaimerText>
            </OptionalExtrasContentWrapper>
          )}

          {videosCTACards.length > 0 && (
            <OptionalExtrasContentWrapper>
              <Box
                display="flex"
                flexDirection="column"
                gap={DesignTokenSizeEnum.static250}
                mb={DesignTokenSizeEnum.static350}
                b960={{ mb: DesignTokenSizeEnum.static515 }}
              >
                <Text
                  tag={TextTag.h3}
                  appearance={TokenTextAppearance.headline300}
                  color={TokenTextColor.inherit}
                  bold
                >
                  {contentManager.get('technology-videos-section-title')}
                </Text>
                <Text
                  tag={TextTag.p}
                  appearance={TokenTextAppearance.bigcopy100}
                  color={TokenTextColor.inherit}
                >
                  {contentManager.get('technology-videos-section-subtitle')}
                </Text>
              </Box>
              <CTACardCarousel
                bulletPrefix="Technology videos"
                ctaCardData={videosCTACards}
                id="technology-videos-carousel"
                ariaLabel="Technology videos carousel"
                slidesPerPage={techVideosSlides?.slidesPerPage}
                theme="inverted"
                hideNavigationDesktop={techVideosSlides?.hideNavigation}
              />
            </OptionalExtrasContentWrapper>
          )}
        </PageContainer>
      </OptionalExtrasSectionWrapperInner>
      <VideoModal
        isOpen={!!videoId}
        handleClose={() => setVideoId('')}
        videoId={videoId}
        goBackLabel={contentManager.get('ctas-go-back')}
        playerName="youtube-video-embed"
        autoplay
        controls
        navigationHeight={NAVIGATION_HEIGHT}
        videoPlayCallback={(fields) =>
          handleVideoPlay(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoPauseCallback={(fields) =>
          handleVideoPause(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoEndedCallback={(fields) =>
          handleVideoEnded(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoMilestoneCallback={(fields) =>
          handleVideoMilestone(analyticsCommonFields, { ...fields, title })
        }
      />
    </OptionalExtrasSectionWrapper>
  );
}

export default OptionalExtras;
